import { ErrorHandler, Injectable, NgZone } from "@angular/core";
import { SnackbarService } from "@assoanalytics/app/shared/services/snackbar/snackbar.service";
import { UiErrorMessage } from "@assoanalytics/app/core/error/ui-error-message";
import { HttpErrorResponse } from "@angular/common/http";
import { ErrorFormatter } from "@assoanalytics/app/core/utils/error-formatter";

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    private snackbarService: SnackbarService,
    private zone: NgZone,
  ) {}

  static throwError(msg: string, originalError?: any) {
    throw new UiErrorMessage(msg, originalError);
  }

  static lastErrorMessage: string | null = null;

  handleError(error: any): void {
    try {
      console.error(error);

      // Utilizziamo NgZone per assicurarci che la UI venga aggiornata
      this.zone.run(() => {
        if (error instanceof UiErrorMessage) {
          // Mostra il messaggio UI personalizzato
          let displayMessage = error.uiMessage;
          let originalError = null;

          // Se c'è un errore originale, aggiungi dettagli
          if (error.originalError) {
            const apiMessage = this.extractApiErrorMessage(error.originalError);
            if (apiMessage && apiMessage !== displayMessage) {
              originalError = apiMessage;
            }
          }

          this.snackbarService.error(
            displayMessage,
            "CLOSE",
            originalError ?? "",
            8000,
            true,
          );
        } else if (error instanceof Error) {
          // Gestione specifica per errori HTTP catturati dall'interceptor
          this.snackbarService.error(error.message, "CLOSE", "", 8000, true);
        } else if (error instanceof HttpErrorResponse) {
          // Gestione specifica per errori HTTP non catturati dall'interceptor
          const formattedError = ErrorFormatter.formatHttpError(error);
          this.snackbarService.error(
            "Comunication error",
            "CLOSE",
            formattedError,
            8000,
            true,
          );
        } else {
          // Fallback per altri tipi di errori
          const msg = error?.message || "Generic error";
          this.snackbarService.error(msg, "CLOSE", "", 8000, true);
        }
      });
    } catch (e) {
      // Fallback in caso di errore durante la gestione dell'errore
      // Anche qui usiamo NgZone per sicurezza
      this.zone.run(() => {
        this.snackbarService.error("Generic error", "CLOSE", "", 8000, true);
      });
    }
  }

  private extractApiErrorMessage(error: any): string | null {
    try {
      if (!error) return null;

      // Gestione errori HTTP con il formatter
      if (error instanceof HttpErrorResponse) {
        return ErrorFormatter.formatHttpError(error);
      }

      // Gestione oggetti errore
      if (typeof error === "object") {
        return (
          error.message ||
          error.error ||
          (typeof error.toString === "function" ? error.toString() : null)
        );
      }

      // Gestione stringhe
      if (typeof error === "string") {
        return error;
      }

      return null;
    } catch (e) {
      console.error("Error extracting API message:", e);
      return "Error extracting API message";
    }
  }
}
